/**
 * Wire contract between the embed script (running on the restaurant's site)
 * and the booking app (running inside the iframe it creates). Both sides import
 * these names from here so a rename cannot land on one side only.
 *
 * This file must stay dependency-free: the embed script bundles it, and nothing
 * under embed/ may import from src/.
 */

/** How the booking app was reached. Sent to Meitre GA as `embed_mode`. */
export type EmbedMode =
  | 'direct'
  | 'widget_v1'
  | 'widget_v2_inline'
  | 'widget_v2_floating'
  | 'external_iframe';

/** URL parameters the v2 script adds to the iframe src, next to `frameId` and `parentOrigin`. */
export const EMBED_V2_PARAMS = {
  embed: 'mt_embed',
  mode: 'mt_mode',
  height: 'mt_height',
  lang: 'mt_lang',
  fresh: 'mt_fresh',
  ref: 'mt_ref',
} as const;

export const EMBED_V2_VALUE = 'v2';

export type EmbedV2Mode = 'inline' | 'floating';

/** `mt_height` value that asks the app to report its height (automatic height mode). */
export const EMBED_AUTO_HEIGHT = 'auto';

/** Legacy v1 widget marker on the iframe src. */
export const WIDGET_V1_PARAM = 'isWidgetView';

/** Google's cross-domain linker parameter: the parent handed us its GA identity. */
export const GA_LINKER_PARAM = '_gl';

/** Envelope of every v2 message from the app to the script. Raw iframes keep the flat v1 resize message. */
export const EMBED_V2_PROTOCOL = 'meitre-embed';
export const EMBED_V2_PROTOCOL_VERSION = 2;
export const EMBED_APP_SOURCE = 'booking-app';

export type EmbedMessageType = 'resize' | 'event' | 'close-request';

export interface EmbedV2Message<T extends EmbedMessageType = EmbedMessageType> {
  source: typeof EMBED_APP_SOURCE;
  protocol: typeof EMBED_V2_PROTOCOL;
  version: typeof EMBED_V2_PROTOCOL_VERSION;
  frameId: string | null;
  type: T;
  payload: T extends 'resize'
    ? { height: number }
    : T extends 'event'
      ? { name: EmbedEventName; params: EmbedEventParams }
      : Record<string, never>;
}

/** Business events published to the site's dataLayer. Names are public contract. */
export const EMBED_EVENTS = {
  widgetOpen: 'meitre_widget_open',
  bookingStep: 'meitre_booking_step',
  reservationConfirmed: 'meitre_reservation_confirmed',
  waitlistJoined: 'meitre_waitlist_joined',
  giftCardPurchased: 'meitre_gift_card_purchased',
} as const;

export type EmbedEventName = (typeof EMBED_EVENTS)[keyof typeof EMBED_EVENTS];

/**
 * The only params an event may carry. No personal data: no name, email, phone
 * or notes — a site's dataLayer is read by every tag on it.
 */
export const EMBED_EVENT_PARAM_KEYS = [
  'transaction_id',
  'party_size',
  'value',
  'currency',
  'step',
  'restaurant',
  'embed_mode',
] as const;

export type EmbedEventParams = Partial<Record<(typeof EMBED_EVENT_PARAM_KEYS)[number], string | number>>;

/** Stable step names, independent of the app's routes. */
export type BookingStep =
  | 'select_diners'
  | 'select_date'
  | 'select_service'
  | 'select_menu'
  | 'select_area'
  | 'select_experience'
  | 'select_time'
  | 'pairings'
  | 'personalize'
  | 'payment'
  | 'confirmation'
  | 'waitlist'
  | 'gift_card';
